import { FormEvent, useEffect, useRef, useState } from 'react';
import { getAuth, OAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';

import { sanitizeEmail } from '@/lib/utils/email';
import { Card } from '@/components/gradient-layout/cards';
import { Input } from '@/components/form/input';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/form/checkbox';
import { useToastContext } from '@/components/toast/toast-context';
import { useSsoProviderIdForEmailLazyQuery } from '@/generated/graphql';

import { isPopupCancellation } from './is-popup-cancellation';

import commonStyles from '../public.module.scss';

type LoginStep = 'email' | 'password';

export const PasswordLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<LoginStep>('email');
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const auth = getAuth();
  const addToast = useToastContext();
  const [getSsoProviderIdForEmail] = useSsoProviderIdForEmailLazyQuery();

  useEffect(() => {
    if (step === 'password') {
      passwordRef.current?.focus();
    }
  }, [step]);

  const showError = (title: string, content: string) => {
    addToast({
      title,
      content: () => content,
      kind: 'error',
    });
  };

  const continueWithEmail = async () => {
    const result = await getSsoProviderIdForEmail({
      variables: { email },
      fetchPolicy: 'no-cache',
    });
    const providerId = result.data?.ssoProviderIdForEmail;

    if (result.error !== undefined || providerId === undefined) {
      throw result.error ?? new Error('SSO provider discovery returned no data');
    }

    if (providerId === null) {
      setStep('password');
      return;
    }

    const provider = new OAuthProvider(providerId);
    provider.setCustomParameters({ login_hint: email });
    provider.addScope('email');
    provider.addScope('profile');
    await signInWithPopup(auth, provider);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (step === 'email') {
        await continueWithEmail();
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      if (isPopupCancellation(error)) {
        return;
      }

      if (step === 'email') {
        showError(
          'Could not continue',
          'Try again or contact your administrator if the issue persists.',
        );
      } else {
        showError('Bad login', 'Email or password is incorrect.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <div className={commonStyles.title}>Log in with email</div>

        <div className={commonStyles.fields}>
          <Input
            type="email"
            size="large"
            placeholder="Email"
            autoComplete="username"
            required
            readOnly={isSubmitting}
            value={email}
            onChange={(event) => {
              setEmail(sanitizeEmail(event.target.value));
              setPassword('');
              setStep('email');
            }}
          />

          {step === 'password' && (
            <>
              <Input
                ref={passwordRef}
                type={isShowingPassword ? 'text' : 'password'}
                size="large"
                placeholder="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />

              <label className={commonStyles.showPassword}>
                <Checkbox
                  checked={isShowingPassword}
                  onChange={() => setIsShowingPassword(() => !isShowingPassword)}
                />{' '}
                Show password
              </label>
            </>
          )}
        </div>

        <Button type="submit" className={commonStyles.button} size="large" loading={isSubmitting}>
          {step === 'email' ? 'Continue' : 'Log in'}
        </Button>
      </Card>
    </form>
  );
};
